import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { detailText } from '../utils/apiError';

export const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // options.action = { label, onClick } draws a button in the toast (e.g. the
  // billing link of a user-limit refusal, the checkout link of an unlicensed module).
  const showToast = useCallback((message, type = 'info', duration = 4000, options = {}) => {
    const id = Date.now();
    // Callers often pass `error.response.data.detail`, which can be an object
    // or a list; rendering that crashed the whole page.
    const text = typeof message === 'string' || React.isValidElement(message)
      ? message
      : detailText(message) || '';
    const toast = { id, message: text, type, duration, action: options?.action || null };
    
    setToasts(prev => [...prev, toast]);

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Helper methods for different types
  const success = useCallback((message, duration, options) => showToast(message, 'success', duration, options), [showToast]);
  const error = useCallback((message, duration, options) => showToast(message, 'error', duration, options), [showToast]);
  const warning = useCallback((message, duration, options) => showToast(message, 'warning', duration, options), [showToast]);
  const info = useCallback((message, duration, options) => showToast(message, 'info', duration, options), [showToast]);

  // A stable value: pages list `toast` in hook dependencies, so a new object
  // per render would re-run their data loads after every toast (and a failing
  // load would then loop: error toast -> reload -> error toast ...).
  const value = useMemo(
    () => ({ showToast, removeToast, success, error, warning, info }),
    [showToast, removeToast, success, error, warning, info],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-2">
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

const Toast = ({ toast, onClose }) => {
  const { t } = useTranslation();
  const { message, type, action } = toast;

  const typeStyles = {
    success: {
      bg: 'bg-green-500',
      icon: '✓',
      border: 'border-green-600'
    },
    error: {
      bg: 'bg-red-500',
      icon: '✕',
      border: 'border-red-600'
    },
    warning: {
      bg: 'bg-yellow-500',
      icon: '⚠',
      border: 'border-yellow-600'
    },
    info: {
      bg: 'bg-blue-500',
      icon: 'ℹ',
      border: 'border-blue-600'
    }
  };

  const style = typeStyles[type] || typeStyles.info;

  return (
    <div
      className={`${style.bg} ${style.border} border-l-4 text-white px-6 py-4 rounded-lg shadow-lg min-w-[300px] max-w-md flex items-start gap-3 animate-slide-in`}
      role="alert"
    >
      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-white bg-opacity-20 rounded-full text-lg font-bold">
        {style.icon}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{message}</p>
        {action?.label && (
          <button
            type="button"
            onClick={() => { onClose(); action.onClick?.(); }}
            className="mt-2 text-sm font-semibold underline hover:no-underline"
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        onClick={onClose}
        className="flex-shrink-0 text-white hover:text-gray-200 transition"
        aria-label={t('common.close')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default ToastProvider;
